/**
 * Giriş adresleri: engellenen alan adından yayındakine taşınma.
 *
 * Kurulumun oyuncuya açık adresi engellenince yenisine geçilir
 * (`betrun301` → `betrun302`). Eski adreste kalan ziyaretçi kapalı bir
 * kapıya bakmasın diye yayındaki adrese taşınır.
 *
 * Kararı **sunucu** verir: `/settings` yanıtındaki `entry_domain.redirect`,
 * isteğin geldiği host panelde kayıtlı bir giriş adresiyken ve yayındaki
 * adres o değilken açılır (bkz. backend/services/entry_domains.py). İstemci
 * bu kararı yeniden hesaplamaz — listeyi hiç görmez, dolayısıyla yanlış
 * yazılmış tek bir kayıt yüzünden yerel kurulumun ya da panel adresinin
 * kendini boşluğa atması mümkün değildir.
 *
 * İki sınır burada durur:
 *
 * - **Panel taşınmaz.** `/admin` altındaki bir oturum yönlendirilirse
 *   yönetici, adresi düzeltmek için girdiği ekrandan atılır. Muafiyet
 *   yalnızca panel **bu adreste gerçekten açılıyorsa** geçerlidir
 *   (`panelRoutes`, `adminDomain.adminRoutesVisible`): panel ayrı bir alan
 *   adına kilitliyken `/admin` portalın 404 sayfasıdır ve sitenin kabuğunu
 *   (menü, bakiye, Para Yatır) çizer — kapatılan adres o yoldan sitenin
 *   tamamını açıyordu (backofficebetrun, 05.10.2026).
 * - **Yol korunur.** Oyuncu bir kampanya ya da oyun bağlantısıyla gelmiş
 *   olabilir; taşınma onu her seferinde ana sayfaya atmamalıdır.
 *
 * **Listedeki adres yayına alınmadan siteyi açmaz** ve bu, paneldeki
 * anahtarlara bağlı değildir: sunucu "taşın" derse taşınılır, "yayında adres
 * yok" (`inactive`) derse site hiç çizilmez ve bilgi ekranı görünür.
 */

const ADMIN_PREFIX = '/admin';

/** Taşınılacak tam adres; taşınmaya gerek yoksa boş dize. */
export const entryRedirectUrl = (entry, location, panelRoutes = true) => {
  if (!entry || !entry.redirect) return '';
  const target = String(entry.active_url || '').trim();
  if (!/^https?:\/\//i.test(target)) return '';
  const path = location?.pathname || '/';
  if (isAdminPath(location, panelRoutes)) return '';
  let base;
  try {
    base = new URL(target);
  } catch {
    return '';
  }
  // Sunucu "taşın" dediyse host zaten farklıdır; yine de kontrol edilir —
  // eşitken yönlendirme sonsuz bir döngü olurdu.
  if (base.hostname === location?.hostname) return '';
  return `${base.origin}${path}${location?.search || ''}${location?.hash || ''}`;
};

function isAdminPath(location, panelRoutes = true) {
  if (!panelRoutes) return false;
  const path = location?.pathname || '/';
  return path === ADMIN_PREFIX || path.startsWith(`${ADMIN_PREFIX}/`);
}

/**
 * Adres panelden kapatıldı (Sunucudan kaldır / Sil): site çizilmez ve
 * ziyaretçi taşınmaz — kapatılan adres "kullanılmıyor" ekranını gösterir.
 */
export const entryDomainClosed = (entry, location, panelRoutes = true) => (
  Boolean(entry && entry.closed) && !isAdminPath(location, panelRoutes)
);

/** Adres listede ama yayında adres yok: site çizilmez, bilgi ekranı görünür. */
export const entryDomainInactive = (entry, location, panelRoutes = true) => (
  Boolean(entry && entry.inactive) && !isAdminPath(location, panelRoutes)
);

/**
 * Portal bu adreste çizilmeli mi? Taşınma başlamışsa ya da adres yayında
 * değilse hayır — yayına alınmamış adreste sitenin bir karesi bile
 * görünmemelidir.
 */
export const entryDomainHoldsPortal = (entry, location, panelRoutes = true) => (
  Boolean(entryRedirectUrl(entry, location, panelRoutes))
  || entryDomainInactive(entry, location, panelRoutes)
  || entryDomainClosed(entry, location, panelRoutes)
);

/** Gerekliyse ziyaretçiyi yayındaki giriş adresine taşır. */
export const applyEntryRedirect = (entry, panelRoutes = true) => {
  if (typeof window === 'undefined') return false;
  const url = entryRedirectUrl(entry, window.location, panelRoutes);
  if (!url) return false;
  // `replace`: eski adres geçmişte kalmaz, geri hareketi oyuncuyu kapalı
  // kapıya geri götürmez.
  window.location.replace(url);
  return true;
};

export default applyEntryRedirect;
